import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import {
  OrderAssignmentConfigService,
  OrderAssignmentConfiguration,
} from "../api/services/orderAssignmentConfigService";

interface UseOrderAssignmentByAuthStatusReturn {
  configurations: OrderAssignmentConfiguration[];
  loading: boolean;
  error: string | null;
  fetchConfigurations: (
    facilityId: string,
    authStatus: string,
  ) => Promise<OrderAssignmentConfiguration[]>;
}

/**
 * Fetches order assignment configurations for a given facility and
 * authorization status.
 *
 * Wraps `OrderAssignmentConfigService.getConfigurationsByFacilityIdAndAuthStatus`
 * with React state for loading / error tracking.
 *
 * @returns configurations – the most recently fetched results.
 * @returns loading – `true` while a request is in-flight.
 * @returns error – error message on failure, otherwise `null`.
 * @returns fetchConfigurations – triggers the fetch; also returns the data
 *   directly so callers can `await` it.
 */
export const useOrderAssignmentByAuthStatus =
  (): UseOrderAssignmentByAuthStatusReturn => {
    const [configurations, setConfigurations] = useState<
      OrderAssignmentConfiguration[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchConfigurations = useCallback(
      async (facilityId: string, authStatus: string) => {
        setLoading(true);
        setError(null);
        try {
          const data =
            await OrderAssignmentConfigService.getConfigurationsByFacilityIdAndAuthStatus(
              facilityId,
              authStatus,
            );
          console.log("data", data);
          setConfigurations(data);
          return data;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to fetch configurations by auth status";
          setError(errorMessage);
          toast.error(
            "Failed to load order assignment configurations for this auth status",
          );
          setConfigurations([]);
          return [];
        } finally {
          setLoading(false);
        }
      },
      [],
    );

    return { configurations, loading, error, fetchConfigurations };
  };
